import type { QueryClient } from "@tanstack/react-query";
import { pendingSignalCountKey } from "@/hooks/usePendingSignalCount";
import type { MarketSignal } from "@/types/marketSignal.type";

/**
 * Cache surgery for a brand's market signals: the one list every Signals
 * surface (the board, Home, the workshop's context pickers) reads, plus the
 * sidebar's pending badge.
 */

export const marketSignalsKey = (brandId: string | null) => ["marketSignals", brandId];

/** Ids of signals this tab created and the server has not confirmed yet. */
const OPTIMISTIC_PREFIX = "optimistic-signal-";
let optimisticSeq = 0;

export const optimisticSignalId = () => `${OPTIMISTIC_PREFIX}${Date.now()}-${++optimisticSeq}`;

export const isOptimisticSignal = (id: string) => id.startsWith(OPTIMISTIC_PREFIX);

export function patchSignal(
  qc: QueryClient,
  brandId: string | null,
  signalId: string,
  patch: Partial<MarketSignal>,
) {
  qc.setQueryData<MarketSignal[]>(marketSignalsKey(brandId), (signals) =>
    signals?.map((s) => (s.id === signalId ? { ...s, ...patch } : s)),
  );
}

/** Lays one signal over the cache (a server reply, or a rollback's saved copy). */
export function putSignal(qc: QueryClient, brandId: string | null, signal: MarketSignal) {
  patchSignal(qc, brandId, signal.id, signal);
}

export function getSignal(qc: QueryClient, brandId: string | null, signalId: string) {
  return qc.getQueryData<MarketSignal[]>(marketSignalsKey(brandId))?.find((s) => s.id === signalId);
}

export function bumpPendingCount(qc: QueryClient, brandId: string | null, delta: number) {
  qc.setQueryData<number>(pendingSignalCountKey(brandId), (count) =>
    count === undefined ? count : Math.max(0, count + delta),
  );
}

/** Keeps an in-flight read from landing on top of an optimistic write. Await before patching. */
export function cancelSignalFetches(qc: QueryClient, brandId: string | null) {
  return Promise.all([
    qc.cancelQueries({ queryKey: marketSignalsKey(brandId) }),
    qc.cancelQueries({ queryKey: pendingSignalCountKey(brandId) }),
  ]);
}

// ---------------------------------------------------------------- write gate
//
// The calendar's rule, for one list: a refresh that lands while this tab has a
// signal write in flight may carry the list from before it and revert the
// optimistic change. So every signal write carries `signalsWriteKey`, realtime
// hints and agent tools refresh through `reconcileSignals`, and while a write
// runs the refresh is held for the last write to settle.

/** The mutationKey of every write that patches a brand's signals. */
export const signalsWriteKey = (brandId: string | null) => ["marketSignalsWrite", brandId];

/** Brands whose refresh was held back while a write ran. */
const dirtyBrands = new Set<string>();

function pendingWrites(qc: QueryClient, brandId: string): number {
  return qc.isMutating({ mutationKey: signalsWriteKey(brandId) });
}

function refresh(qc: QueryClient, brandId: string, list: boolean) {
  if (list) void qc.invalidateQueries({ queryKey: marketSignalsKey(brandId) });
  void qc.invalidateQueries({ queryKey: pendingSignalCountKey(brandId) });
}

/** Refetch the list and the badge, unless a write is running: then the last write to settle does it. */
export function reconcileSignals(qc: QueryClient, brandId: string | null) {
  if (!brandId) return;
  if (pendingWrites(qc, brandId) > 0) {
    dirtyBrands.add(brandId);
    return;
  }
  dirtyBrands.delete(brandId);
  refresh(qc, brandId, true);
}

/**
 * A signal write is done (call it from `onSettled`, once). Only the last write
 * on the brand to settle acts: it refreshes the badge, and the list too when a
 * refresh was held back or the write asks for one (`refetch`, e.g. after a
 * rollback, or when it has no server copy to lay over the cache).
 */
export function settleSignalsWrite(
  qc: QueryClient,
  brandId: string | null,
  opts: { refetch: boolean },
) {
  if (!brandId) return;
  // The settling mutation still counts itself here, hence > 1.
  if (pendingWrites(qc, brandId) > 1) {
    if (opts.refetch) dirtyBrands.add(brandId);
    return;
  }
  const dirty = dirtyBrands.delete(brandId);
  refresh(qc, brandId, dirty || opts.refetch);
}
